/**
 * Upload one asset file to the Worker via the Pages catch-all proxy.
 *
 * The Worker requires `Content-SHA256: <hex sha256>` and verifies it
 * against the body bytes (Requirement 16.4 / 34.3). We compute the
 * digest in-browser via Web Crypto, then PUT the bytes through XHR so
 * the editor can render a real progress indicator — `fetch()` does not
 * expose upload progress events.
 *
 * The request goes to `/admin/v1/packs/:packId/assets/:assetId`. The
 * Pages catch-all (`functions/admin/v1/[[path]].ts`) intercepts that
 * URL, attaches the bearer cookie as `Authorization: Bearer …`, and
 * forwards the body upstream — so from the SPA's point of view this is
 * just a same-origin XHR.
 *
 * Validates Requirements 16.4, 22.2, 25.1, 29.1.
 */

import { ApiError } from './api';
import { useReauthStore } from './reauth';

/**
 * Compute the lowercase hex SHA-256 of a `File`. Reads the file fully
 * into memory; the upload route already caps at 8 MB so this is bounded.
 */
export async function sha256HexOfFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  const view = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < view.length; i++) {
    hex += view[i]!.toString(16).padStart(2, '0');
  }
  return hex;
}

export interface UploadAssetParams {
  packId: string;
  assetId: string;
  file: File;
  sha256: string;
  /** Called with progress in [0, 1] as the upload streams. */
  onProgress?: (fraction: number) => void;
  /** Optional abort signal — wires into XHR via `xhr.abort()`. */
  signal?: AbortSignal;
}

/**
 * Server-decoded JSON body for a successful upload (200) or any error
 * envelope. Mirrors `serializeAsset(...)` from the Worker on success.
 */
export interface UploadedAsset {
  packId: string;
  assetId: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  width: number;
  height: number;
  durationMs: number | null;
  position: number;
  r2Key: string;
  state: 'staged' | 'published' | 'deleted';
  uploadedAt: string;
}

/**
 * PUT one asset file. Resolves with the persisted asset row on 2xx.
 *
 * On 401, re-uses the dashboard's standard re-auth modal (mirroring
 * `apiFetch`) and retries the upload exactly once. On any other non-2xx,
 * rejects with an `ApiError` carrying the upstream `code` / `message` /
 * `requestId` so the editor can render a Vietnamese reason next to the
 * file row.
 */
export async function uploadAssetFile(
  params: UploadAssetParams,
): Promise<UploadedAsset> {
  try {
    return await uploadOnce(params);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      // Pause behind the re-auth modal exactly like apiFetch does. If the
      // user successfully signs back in, retry once.
      try {
        await useReauthStore.getState().requestReauth();
      } catch {
        throw err;
      }
      return uploadOnce(params);
    }
    throw err;
  }
}

function uploadOnce(params: UploadAssetParams): Promise<UploadedAsset> {
  const url = `https://skey-worker.sonhangtravel.workers.dev/admin/v1/packs/${encodeURIComponent(
    params.packId,
  )}/assets/${encodeURIComponent(params.assetId)}`;

  return new Promise<UploadedAsset>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.responseType = 'text';
    xhr.setRequestHeader('Content-Type', params.file.type);
    xhr.setRequestHeader('Content-SHA256', params.sha256);
    const token = window.localStorage.getItem('skey_admin_token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    if (params.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && event.total > 0) {
          params.onProgress!(Math.min(1, event.loaded / event.total));
        }
      });
    }

    xhr.addEventListener('load', () => {
      const status = xhr.status;
      const text = xhr.responseText;
      let parsed: unknown = null;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = null;
      }
      if (status >= 200 && status < 300) {
        if (parsed && typeof parsed === 'object') {
          resolve(parsed as UploadedAsset);
          return;
        }
        reject(
          new ApiError(status, {
            error: {
              code: 'invalid_response',
              message: 'Phản hồi từ máy chủ không hợp lệ',
            },
          }),
        );
        return;
      }
      const body =
        parsed && typeof parsed === 'object'
          ? (parsed as { error?: { code?: string; message?: string; requestId?: string } })
          : { error: { code: 'unknown_error', message: text || xhr.statusText } };
      reject(new ApiError(status, body));
    });

    xhr.addEventListener('error', () => {
      reject(
        new ApiError(0, {
          error: {
            code: 'network_down',
            message: 'Mất kết nối tới máy chủ',
          },
        }),
      );
    });

    xhr.addEventListener('abort', () => {
      reject(
        new ApiError(0, {
          error: {
            code: 'upload_aborted',
            message: 'Tải lên đã bị huỷ',
          },
        }),
      );
    });

    if (params.signal) {
      if (params.signal.aborted) {
        xhr.abort();
        return;
      }
      params.signal.addEventListener('abort', () => xhr.abort());
    }

    xhr.send(params.file);
  });
}

/**
 * Format a byte count as a Vietnamese-friendly human string. Falls back
 * to "B" for tiny files. Used by the upload row + asset tooltip.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const fmt = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 });
  if (bytes < 1024 * 1024) return `${fmt.format(bytes / 1024)} KB`;
  return `${fmt.format(bytes / (1024 * 1024))} MB`;
}
